// Field Time Report PDF export + share.
//
// Captures the report's off-screen page nodes (816×1056 px, letter @ 96dpi)
// to PNGs, composes them into a letter-sized PDF with jsPDF, then hands off:
//   NATIVE (iOS): write to the cache dir and open the OS share sheet via
//     @capacitor/share — Mail / Messages / Print / Save to Files come free
//     for a PDF file URI.
//   WEB: Web Share API with a File when available; otherwise download.
//
// Mirrors src/lib/wrapped/wrapped-share.ts; failures are the caller's to
// surface (the modal shows a toast and never crashes).

import { toJpeg } from 'html-to-image';
import { Capacitor } from '@capacitor/core';
import type { FieldTimeReportData } from './field-time-report';

// US letter in points; the 816×1056 @ 96dpi pages map onto it exactly.
const PAGE_W = 612;
const PAGE_H = 792;

function fileName(data: FieldTimeReportData): string {
  const safe = (s: string) => s.replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/(^-|-$)/g, '');
  return `${safe(data.playerName) || 'player'}-field-time-${safe(data.seasonLabel)}.pdf`;
}

export async function shareFieldTimeReport(
  pageNodes: HTMLElement[],
  data: FieldTimeReportData,
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });

  for (let i = 0; i < pageNodes.length; i++) {
    // JPEG, not PNG: a white document page compresses ~50× smaller with no
    // visible loss, and the PDF has to travel by email/Messages.
    const jpeg = await toJpeg(pageNodes[i], {
      pixelRatio: 2,
      quality: 0.92,
      backgroundColor: '#FFFFFF',
      cacheBust: true,
    });
    if (i > 0) doc.addPage();
    doc.addImage(jpeg, 'JPEG', 0, 0, PAGE_W, PAGE_H);
  }

  const name = fileName(data);
  const title = `${data.playerName}: Field Time Report`;

  if (Capacitor.isNativePlatform()) {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const { Share } = await import('@capacitor/share');
    const base64 = doc.output('datauristring').split(',')[1];
    const writeRes = await Filesystem.writeFile({
      path: name,
      data: base64,
      directory: Directory.Cache,
    });
    await Share.share({ title, url: writeRes.uri, dialogTitle: 'Share Field Time Report' });
    return;
  }

  // Web: prefer native Web Share with a File (mobile browsers), else download.
  const blob = doc.output('blob');
  const file = new File([blob], name, { type: 'application/pdf' });
  const nav = navigator as any;
  if (nav.canShare && nav.canShare({ files: [file] })) {
    try {
      await nav.share({ files: [file], title });
      return;
    } catch (err: any) {
      // User cancelled the sheet → done. Real failure → fall through to download.
      if (err?.name === 'AbortError') return;
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
